/**
 * Identity's password policy, mirrored for live feedback while a password is typed
 * (authentication-service: app/infrastructure/security/passwords.py, `PasswordPolicy`).
 * Identity stays authoritative: it also screens breached passwords, which only the server can do.
 */

export const MIN_PASSWORD = 12; // IDENTITY_PASSWORD_MIN_LENGTH default
export const MAX_PASSWORD = 256;
const MIN_DISTINCT = 4;
const GUESSABLE = ["cortexgrip", "password", "123456", "qwerty"];

export type PasswordRuleId = "length" | "variety" | "email" | "common";

export interface PasswordRule {
  id: PasswordRuleId;
  label: string;
  met: boolean;
}

/** NFKC-normalised code points, the way Identity counts (`len()` in Python, not UTF-16 units). */
function codePoints(password: string): string[] {
  return [...password.normalize("NFKC")];
}

/**
 * Each requirement and whether `password` meets it. An empty password meets none. The e-mail rule is
 * listed only when the form knows the address (`email` is `undefined` where it doesn't, e.g. a reset link).
 */
export function passwordRules(password: string, email?: string): PasswordRule[] {
  const chars = codePoints(password);
  const typed = chars.length > 0;
  const lowered = chars.join("").toLowerCase();
  const rules: PasswordRule[] = [
    { id: "length", label: `At least ${MIN_PASSWORD} characters`, met: chars.length >= MIN_PASSWORD },
    {
      id: "variety",
      label: `At least ${MIN_DISTINCT} different characters`,
      met: new Set(chars).size >= MIN_DISTINCT,
    },
  ];
  if (email !== undefined) {
    const local = email.trim().toLowerCase().split("@", 1)[0] ?? "";
    rules.push({
      id: "email",
      label: "Doesn't contain your e-mail address",
      met: typed && !(local.length >= 4 && lowered.includes(local)),
    });
  }
  rules.push({
    id: "common",
    label: "Doesn't contain easy-to-guess words like “password” or “123456”",
    met: typed && !GUESSABLE.some((word) => lowered.includes(word)),
  });
  return rules;
}

/** Whether Identity's policy (bar the server-only breach check) accepts `password`. */
export function meetsPasswordPolicy(password: string, email?: string): boolean {
  return codePoints(password).length <= MAX_PASSWORD && passwordRules(password, email).every((r) => r.met);
}
